import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Edit3 } from 'lucide-react';

/**
 * Edit Reason Modal
 *
 * Generic modal for capturing edit reasons for safety-critical records.
 * Required for MCA compliance - ISM Code Element 9, WBC3 SMS requirements.
 * All changes to safety records must be traceable with documented reasons.
 *
 * @param {boolean} isOpen - Modal visibility
 * @param {function} onClose - Close handler (cancel)
 * @param {function} onConfirm - Confirm handler with reason string
 * @param {object} item - Item being edited { id, name, type, details... }
 * @param {string} recordType - Type of record (training, drill, defect, etc.)
 */
const EditReasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  item = {},
  recordType = 'record'
}) => {
  const [reason, setReason] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Validation - reason is required
  const canSubmit = reason.trim().length >= 5;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm(reason.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey && canSubmit) {
      handleConfirm();
    }
  };

  // Get display name for the item
  const getItemName = () => {
    return item.topic || item.name || item.type || item.label || 'this record';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Edit3 className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Reason for Edit
                </h3>
                <p className="mt-0.5 text-sm text-gray-600">
                  Safety records require documented changes
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Item Details */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="text-sm">
              <p className="font-medium text-gray-900">Editing {recordType}:</p>
              <p className="mt-1 text-gray-700 font-semibold">
                {getItemName()}
              </p>
              {item.date && (
                <p className="mt-0.5 text-gray-600 text-xs">
                  Recorded: {new Date(item.date).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            {/* Info message */}
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <div className="ml-3 text-sm text-blue-700">
                  <p className="font-medium">Compliance Requirement</p>
                  <p className="mt-1">
                    All changes to safety-critical records must be documented with a reason.
                    This ensures full traceability for MCA inspections.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason Input */}
            <div>
              <label htmlFor="editReason" className="block text-sm font-medium text-gray-900 mb-2">
                Why are you making this change? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="editReason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={4}
                placeholder="e.g., Correcting trainee name typo, updating certificate details, adding missing information..."
                className="block w-full rounded-md px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm resize-none"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500">
                Minimum 5 characters required. Be specific about what you're changing and why.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <div className="text-xs text-gray-600">
              <AlertTriangle className="inline h-3 w-3 mr-1" />
              This will be recorded in the audit trail
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!canSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Edit3 className="h-4 w-4" />
                Continue to Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditReasonModal;
